import * as React from "react";
import { notification } from "antd";
import { ChevronDown, ChevronUp, Copy, ExternalLink } from "react-feather";
import { observer } from "mobx-react-lite";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { NGInput } from "../../../../newcomponents/input/input";
import { IS_DEPLOYMENT_ENV_TEST } from "../../../../envVariables";
import { NGButton } from "../../../../newcomponents/button/Button";
import { CodeEditor } from "../../../group/identityMatcher/codeEditor";

interface Props {
  draft: Mk8sDraftMobx;
}
const ProviderGCPInstructionsRaw: React.FC<Props> = ({ draft }) => {
  const [state, _setState] = React.useState<"none" | "cli" | "ui">("none");

  const cliSteps = [
    `gcloud iam service-accounts create SA_NAME \
    --project=PROJECT \
    --display-name="SA_NAME"`,
    `gcloud projects add-iam-policy-binding PROJECT \
    --member="serviceAccount:SA_NAME@PROJECT.iam.gserviceaccount.com" \
    --role="roles/compute.instanceAdmin.v1"`,
    `gcloud projects add-iam-policy-binding PROJECT \
    --member="serviceAccount:SA_NAME@PROJECT.iam.gserviceaccount.com" \
    --role="roles/compute.loadBalancerAdmin"`,
    `gcloud projects add-iam-policy-binding PROJECT \
    --member="serviceAccount:SA_NAME@PROJECT.iam.gserviceaccount.com" \
    --role="roles/compute.storageAdmin"`,
    `gcloud projects add-iam-policy-binding PROJECT \
    --member="serviceAccount:SA_NAME@PROJECT.iam.gserviceaccount.com" \
    --role="roles/compute.securityAdmin"`,
    `gcloud projects add-iam-policy-binding PROJECT \
    --member="serviceAccount:SA_NAME@PROJECT.iam.gserviceaccount.com" \
    --role="roles/compute.networkViewer"`,
    `gcloud iam service-accounts keys create SA_NAME.json \
    --iam-account=SA_NAME@PROJECT.iam.gserviceaccount.com`,
  ];

  function setState(from: "cli" | "ui") {
    if (state === from) {
      _setState("none");
    } else {
      _setState(from);
    }
  }

  async function copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    notification.success({
      message: "Copied to clipboard",
    });
  }

  return (
    <>
      <div className="mt-1 mb-2">
        <button className="block ngfocus color-link" onClick={() => setState("cli")}>
          How do I get my GCP Service Account Key with Google Cloud CLI?{" "}
          {state === "cli" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>

        {state === "cli" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            <li className="list-decimal my-2">
              Install the{" "}
              <a
                className="underline hover:underline focus:underline ngfocus color-link"
                href={"https://docs.cloud.google.com/sdk/docs/install-sdk"}
                target={"_blank"}
              >
                Google Cloud CLI
              </a>{" "}
              and{" "}
              <a
                className="underline hover:underline focus:underline ngfocus color-link"
                href={"https://docs.cloud.google.com/docs/authentication"}
                target={"_blank"}
              >
                configure authentication
              </a>
              .
            </li>
            <li className="my-2 list-decimal">
              Execute the following commands by replacing the SA_NAME and PROJECT placeholders.
            </li>
            {cliSteps.map((_cliStep) => {
              let cliStep = _cliStep;
              return (
                <>
                  <div className="flex items-center my-2" key={cliStep}>
                    <input className="flex-grow px-4 py-2 border rounded" disabled={true} value={cliStep} />
                    <button className={`ml-2 focus`} onClick={() => copyToClipboard(cliStep)}>
                      <Copy className="ml-1 feather-icon color-link-hover" style={{ transform: "translateY(2px)" }} />
                    </button>
                  </div>
                </>
              );
            })}
            <li className="my-2 list-decimal">Create a Secret of type GCP with the created service account</li>
          </ul>
        ) : null}
        <button className="block ngfocus color-link" onClick={() => setState("ui")}>
          How do I get my GCP Service Account Key from GCP Console UI?{" "}
          {state === "ui" ? (
            <ChevronUp style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          ) : (
            <ChevronDown style={{ transform: "translateY(2px)" }} className="feather-icon inline-block" />
          )}
        </button>
        {state === "ui" ? (
          <ul className="mt-1 mb-4 px-4 py-2 ml-4">
            <li className="list-decimal my-2">Navigate to the IAM & Admin dashboard.</li>
            <li className="list-decimal my-2">
              In the navigation pane, choose <span className="font-semibold">Service Accounts</span>.
            </li>
            <li className="list-decimal my-2">Create a service account by giving it a name and an id.</li>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 800 }}>
              <img
                className="object-container"
                style={{ width: 800 }}
                src={"/resources/howto/gcp_serviceaccount.png"}
                alt={"create a service account"}
              />
            </div>
            <li className="list-decimal my-2">
              Grant the roles displayed in the following image to your new service account.
            </li>
            <div className="flex items-center justify-center mb-2 border" style={{ width: 800 }}>
              <img
                className="object-container"
                style={{ width: 800 }}
                src={"/resources/howto/gcp_access.png"}
                alt={"create a service account"}
              />
            </div>
            <li className="my-2 list-decimal">Create a Secret of type GCP with the created service account</li>
          </ul>
        ) : null}
      </div>
    </>
  );
};

export const ProviderGCPInstructions = observer(ProviderGCPInstructionsRaw);
